import React, { useState } from "react";
import { useLiquidityZoneData } from "./useLiquidityZoneData";
import styles from "../Chart.module.css";
import { Row, Container, Col } from "reactstrap";
import LiquidityZoneInfo from "../Info/LiquidityZoneInfo";
import { FaInfoCircle } from "react-icons/fa";
import { useAppContext } from "../../../App"; // Import AppContext

const LiquidityZoneChart = () => {

  const {
    currencyPair, setCurrencyPair, timeInterval, setTimeInterval, timeZone, setTimeZone, timeZones, g50Currencies, timeIntervals, Plot, LoadingSpinner, Dropdown, dateRange, setDateRange, handleRelayout, availableDates, setAvailableDates, currentPrice, setCurrentPrice
  } = useAppContext(); // Destructure values from AppContext
  const [zoneThreshold, setZoneThreshold] = useState(0.0005);
  const [persistenceThreshold, setPersistenceThreshold] = useState(5);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const toggleInfoDrawer = () => {
    setIsInfoOpen((prev) => !prev);
  };
  const { chartData, loading, error, refreshCounter } = useLiquidityZoneData(
    currencyPair,
    timeInterval,
    timeZone,
    zoneThreshold,
    persistenceThreshold,
    dateRange, setDateRange, currentPrice, setCurrentPrice
  );

  const handleCurrencyPairChange = (value) => {
    setCurrencyPair(value);
  };
  const handleTimeIntervalChange = (value) => {
    setTimeInterval(value);
  };
  const handleTimeZoneChange = (value) => {
    setTimeZone(value);
  };
  const handleZoneThresholdChange = (e) => {
    setZoneThreshold(parseFloat(e.target.value));
  };
  const handlePersistenceThresholdChange = (e) => {
    setPersistenceThreshold(parseInt(e.target.value, 10));
  };

  if (loading) {
    return <LoadingSpinner message="Loading chart data..." />;
  }

  if (error) {
    return <p style={{ color: "red" }}>{error}</p>;
  }

  return (
    <Container>
      <Row>
        <Col xs={12} md={12}>
          <div className={styles.header}>
            <h2 className={styles.chartTitle}>
              Liquidity Zone Analysis - {currencyPair} {currentPrice}
              <FaInfoCircle
                onClick={toggleInfoDrawer}
                style={{
                  marginLeft: "20px",
                  cursor: "pointer",
                  fontSize: "20px",
                  verticalAlign: "middle",
                }}
                title="Open Liquidity Zone Info"
              />
            </h2>
          </div>
          <LiquidityZoneInfo isOpen={isInfoOpen} toggleDrawer={toggleInfoDrawer} />
        </Col>
      </Row>
      <Row>
        <Col xs={4} md={4}>
          <Dropdown
            label={`Currency`}
            options={g50Currencies.map((currency) => ({
              label: `${currency.country} (${currency.pair})`,
              value: currency.pair,
            }))}
            value={currencyPair}
            onChange={handleCurrencyPairChange}
          />
        </Col>
        <Col xs={4} md={4} > <Dropdown
          label="Time Interval"
          options={timeIntervals}
          value={timeInterval}
          onChange={handleTimeIntervalChange}
        /> </Col>
        <Col xs={4} md={4}>
          <Dropdown
            label="Time Zone"
            options={timeZones.map((zone) => ({
              label: zone.label,
              value: zone.value,
            }))}
            value={timeZone}
            onChange={handleTimeZoneChange}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6} md={6}>
          <div className={styles.sliderContainer}>
            <label htmlFor="zoneThreshold" className={styles.sliderLabel}>
              Zone Threshold: {zoneThreshold.toFixed(4)}
            </label>
          </div>
        </Col>
        <Col xs={6} md={6}>
          <div className={styles.sliderContainer}>
            <label htmlFor="persistenceThreshold" className={styles.sliderLabel}>
              Persistence Threshold: {persistenceThreshold}
            </label>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={6} md={6}>
          <input
            type="range"
            id="zoneThreshold"
            min="0.015"
            max="5"
            step="0.015"
            value={zoneThreshold}
            onChange={handleZoneThresholdChange}
            className={styles.slider}
          />
        </Col>
        <Col xs={6} md={6}>
          <input
            type="range"
            id="persistenceThreshold"
            min="1"
            max="20"
            step="1"
            value={persistenceThreshold}
            onChange={handlePersistenceThresholdChange}
            className={styles.slider}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          {availableDates.start && availableDates.end && (
            <div className={styles.sliderContainer}>
              <label htmlFor="dateRangeSlider" className={styles.sliderLabel}>
                Available Date Range: {new Date(availableDates.start).toLocaleDateString()} -{" "}
                {new Date(availableDates.end).toLocaleDateString()}
              </label>
            </div>
          )}</Col>
      </Row>
      <Row>
        <Col md={12}>
          {availableDates.start && availableDates.end && (
            <div className={styles.sliderContainer}>
              <input
                type="range"
                id="dateRangeSlider"
                min={new Date(availableDates.start).getTime()} // Minimum is the start of available dates
                max={new Date(availableDates.end).getTime()} // Maximum is the end of available dates
                step={86400000} // 1 day in milliseconds
                value={new Date(dateRange.start).getTime()} // Bind slider to the start date
                onChange={(e) => {
                  const start = new Date(parseInt(e.target.value)).toISOString(); // Parse slider value to ISO format
                  setDateRange((prev) => ({ ...prev, start })); // Update the start date
                }}
                className={styles.slider}
              />
            </div>
          )}</Col>
      </Row>
      <Row>
        <Col md={12}>
          {availableDates.start && availableDates.end && (
            <div className={styles.sliderContainer}>
              <label htmlFor="dateRangeSlider" className={styles.sliderLabel}>
                Selected Start Date: {new Date(dateRange.start).toLocaleDateString()}
              </label>
            </div>
          )}</Col>
      </Row>
      <Row>
        <Col md={6}>
          <div>
            {loading && <p>Loading...</p>}
            {error && <p>Error: {error}</p>}
            <p>Data will refresh in: {refreshCounter} seconds</p>
          </div>
        </Col>
        <Col md={6}></Col>
      </Row>
      <Row>
        <Col md={12}>
          {chartData && (
            <Plot
              data={chartData.traces}
              layout={{
                ...chartData.layout,
                margin: { t: 40, r: 40, b: 40, l: 40 },
                autosize: true,
                legend: { orientation: "h", x: 0, y: 40 },
              }}
              onRelayout={handleRelayout} // Listen to relayout events
              config={{ responsive: true, scrollZoom: true }}
              style={{ width: "100%", height: "65vh" }}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
};
export default LiquidityZoneChart;
